import { Box, Card } from '@ksyos/design-system';
import type { IProcessAction } from '../../../../common/interfaces/IProcessAction';
import { ServiceCode } from '../../../../common/enums/ServiceCode';
import { translateKey } from '../../../translate';
import { useQuestionnairesProgress } from '../../../utils/hooks/useQuestionnairesProgress';
import { PageTitle } from '../../elements/atoms/pageTitle/PageTitle';
import { Container } from '../../elements/molecules/container/Container';
import { Main } from '../../elements/molecules/main/Main';
import { PageSection } from '../../elements/molecules/pageSection/PageSection';
import { Footer } from '../../elements/organisms/footer/Footer';
import { Header } from '../../elements/organisms/header/Header';
import { PageWrapper } from '../../elements/organisms/pageWrapper/PageWrapper';
import { QuestionnaireCard } from '../../elements/organisms/questionnaireCard/QuestionnaireCard';
import { CompleteQuestionnaireList } from './components/CompleteQuestionnaireList';
import { getQuestionnaireCardTitle } from './utils';

export function OsasFlexQuestionnaires() {
    const { availableQuestionnaireActions, questionnaireProcessActivities, questionnairesProgress } =
        useQuestionnairesProgress(ServiceCode.OSAS);

    if (!availableQuestionnaireActions) {
        return null;
    }

    return (
        <PageWrapper fullHeight>
            <Header
                backButtonText={translateKey('common.home')}
                backUrl="/"
                hasBackbutton
                hasLogoutButton
            />
            <Main>
                <Container isFluidOnMobile>
                    <PageSection>
                        <PageTitle>Vragen van je slaaparts</PageTitle>
                        <Card>
                            <Card.Body>
                                <Box maxWidth={620}>
                                    {availableQuestionnaireActions.length === 0
                                        ? 'De vragenlijsten zijn afgerond en verzonden. De slaaparts bespreekt de resultaten met jou in een videoconsult. Ksyos belt jou hierover op om een afspraak te maken.'
                                        : 'Je gaat slapen met de slaaprecorder of bent hier al mee begonnen. Beantwoord de vragen om de slaaparts een beter beeld te geven van je klachten. '}
                                </Box>
                            </Card.Body>
                        </Card>
                    </PageSection>
                    <PageSection>
                        <Box
                            display="grid"
                            gap={4}
                            gridTemplateColumns={['1fr', null, '1fr 1fr']}
                        >
                            {availableQuestionnaireActions.map((action) => renderQuestionnaireCard(action))}

                            <CompleteQuestionnaireList />
                        </Box>
                    </PageSection>
                </Container>
            </Main>
            <Footer />
        </PageWrapper>
    );

    function renderQuestionnaireCard(action: IProcessAction) {
        const isLifestyleQuestionnaire = action.label === 'BMI & alcoholconsumptie';
        const questionsEstimatedTime: { [key: string]: string } = {
            'BMI & alcoholconsumptie': '2 minuten',
            ess: '2 minuten',
            hsdq: '5 minuten',
        };

        const activityWithDraft = action.activities.find((activity) => activity.processActivityId);
        const questionnaireProgress = questionnairesProgress.find(
            (item) => item.processActivityId === activityWithDraft?.processActivityId
        );

        const title = getQuestionnaireCardTitle(action.label, isLifestyleQuestionnaire);

        const url = isLifestyleQuestionnaire
            ? `/osas/lifestyleQuestionnaire/${action.id}`
            : `/osas/questionnaires/${action.id}`;

        return (
            <QuestionnaireCard
                answers={
                    isLifestyleQuestionnaire
                        ? getCountLifestyleQuestionnaire(action)
                        : questionnaireProgress?.answerCount
                }
                body={`Nog te doen | ${questionsEstimatedTime[action.label]}`}
                data-automationTestId="questionnaire-card"
                isSent={false}
                key={action.label}
                questions={isLifestyleQuestionnaire ? 4 : questionnaireProgress?.questionCount}
                title={title}
                to={url}
            />
        );
    }

    function getCountLifestyleQuestionnaire(action: IProcessAction) {
        const serviceActivityId = action.activities.find(
            (activity) => activity.customControlCode === 'OSASLifestyleForm'
        )?.id;

        if (serviceActivityId) {
            const data =
                questionnaireProcessActivities.find((item) => item.serviceActivityId === serviceActivityId)
                    ?.customControlData ?? {};

            return data.page as number;
        }

        return 0;
    }
}
